import { ModularSofa } from '@fixtuur/backpack';
import { createAction } from '@reduxjs/toolkit';

/** Core */
export const loadingFinished = 'stella/loading_finished';
export const loadingFinishedAction = createAction(loadingFinished);

export const closeFromError = 'stella/closeFromError';
export const closeFromErrorAction = createAction(closeFromError);

export const globalError = 'stella/globalError';
export const globalErrorAction = createAction<string>(globalError);

/** 3D Scene */
export const user3dSceneInteraction = 'stella/3dSceneInteraction';
export const user3dSceneInteractionAction = createAction(user3dSceneInteraction);

export const dimensionsToggled = 'stella/dimensionsToggle';
export const dimensionsToggledAction = createAction<{ isVisible: boolean }>(dimensionsToggled);

export const undo = 'stella/undo';
export const undoAction = createAction(undo);

export const redo = 'stella/redo';
export const redoAction = createAction(redo);

/** Configure Panel */
export const configureButtonVisible = 'stella/configureButtonVisible';
export const configureButtonVisibleAction = createAction<boolean>(configureButtonVisible);

export const configurePanelToggle = 'stella/configurePanelToggle';
export const configurePanelToggleAction = createAction<boolean>(configurePanelToggle);

export const selectPartToConfigure = 'stella/selectPartToConfigure';
export const selectPartToConfigureAction =
    createAction<{ part: string | null }>(selectPartToConfigure);

export const selectProductCard = 'stella/selectProductCard';
export const selectProductCardAction =
    createAction<{ newItemId: string | null; compatible: boolean; price: string | null }>(
        selectProductCard,
    );

export const addParts = 'stella/addParts';
export const addPartsAction = createAction(addParts);

export const removeParts = 'stella/removeParts';
export const removePartsAction = createAction(removeParts);

export const addPartsLoadingDuration = 'stella/addPartsLoadingDuration';
export const addPartsLoadingDurationAction = createAction<string | number>(addPartsLoadingDuration);

export const changeSelectionsLoadingDuration = 'stella/changeSelectionsLoadingDuration';
export const changeSelectionsLoadingDurationAction = createAction<{
    measure: string;
    solutionMode: string;
    selectionType: string;
    isCompatible: string;
}>(changeSelectionsLoadingDuration);

export const addPartsButtonVisible = 'stella/addPartsButtonVisible';
export const addPartsButtonVisibleAction = createAction<boolean>(addPartsButtonVisible);

export const moreInfoClicked = 'stella/moreInfoClicked';
export const moreInfoClickedAction = createAction<string>(moreInfoClicked);

export const variantQuestionView = 'stella/variantQuestionView';
export const variantQuestionViewAction = createAction<string>(variantQuestionView);

export const variantSelectionApplied = 'stella/variantSelectionApplied';
export const variantSelectionAppliedAction = createAction<string | number | null>(
    variantSelectionApplied,
);

export const variantColourSelectionApplied = 'stella/variantColourSelectionApplied';
export const variantColourSelectionAppliedAction = createAction<string>(
    variantColourSelectionApplied,
);
export const compatibilityToggled = 'stella/compatibility';
export const compatibilityToggledAction = createAction<string>(compatibilityToggled);

/** Sharing */
export const shareModalOpened = 'stella/shareModalOpened';
export const shareModalOpenedAction = createAction(shareModalOpened);

export const shareButtonClicked = 'stella/shareButtonClicked';
export const shareButtonClickedAction = createAction<'email' | 'whatsapp' | 'facebook'>(
    shareButtonClicked,
);

export const shareLinkCopied = 'stella/shareLinkCopied';
export const shareLinkCopiedAction = createAction(shareLinkCopied);

/** AR */
export const viewInAr = 'stella/view_in_ar';
export const viewInArAction = createAction(viewInAr);

export const openArDrawer = 'stella/open_ar_drawer';
export const openArDrawerAction = createAction(openArDrawer);

export const closeArDrawer = 'stella/close_ar_drawer';
export const closeArDrawerAction = createAction(closeArDrawer);

/** for capturing SKU Data */
export const skuView = 'stella/sku_view';
export const skuViewAction = createAction(skuView);

export const addToBasket = 'stella/add_to_basket';
export const addToBasketAction = createAction(addToBasket);

/* --- MODULAR CONFIGURATION SPECIFIC --- */
/* TODO: Need to add the following properties into our Modular Configurator events, as per ENG-245:
 * - List of SKUs -> Should we do this via basket?
 * - Range Selected -> Can this be done via SDK? Or by storing the 'configuratorId' in store?
 * - Global Selections -> Are we going to store this in store possibly?
 * - Basket Price -> Should we do this via basket?
 * - Total Number Of Parts -> Should we do this via basket? Each BasketProduct has a 'quantity' value.
 */

/* --- Piece Interactions --- */
export const selectPiece = 'stella/select_piece';
export const selectPieceAction = createAction<{
    selectedPiecePosition: number;
}>(selectPiece);

export const changePiece = 'stella/change_piece';
export const changePieceAction = createAction<{
    newPiece: string;
    newPiecePosition: number;
}>(changePiece);

export const addButtonClick = 'stella/add_button_click';
export const addButtonClickAction = createAction<{
    selectedPiecePosition: number | null;
    buttonSide: string;
}>(addButtonClick);

export const addPiece = 'stella/add_piece';
export const addPieceAction = createAction<{
    addedPiece: string;
    addedPiecePosition: number;
}>(addPiece);

export const removePiece = 'stella/remove_piece';
export const removePieceAction = createAction<{
    removedPiecePosition: number;
}>(removePiece);

/* --- Global Selections Interactions --- */
export const openSelectionsMenu = 'stella/open_global_selections_menu';
export const openSelectionsMenuAction = createAction(openSelectionsMenu);

export const changeSelection = 'stella/change_global_selections';
export const changeSelectionAction = createAction<{
    newSelections: ModularSofa.PropertySelections;
}>(changeSelection);

/* --- App Interactions --- */
export const viewBasket = 'stella/view_basket';
export const viewBasketAction = createAction(viewBasket);

export const viewInRoom = 'stella/view_in_room';
export const viewInRoomAction = createAction(viewInRoom);

export const viewCatalogueTab = 'stella/view_catalogue_tab';
export const viewCatalogueTabAction = createAction<{
    catalogueTab: string;
}>(viewCatalogueTab);

export const viewCataloguePanel = 'stella/view_catalogue_panel';
export const viewCataloguePanelAction = createAction<{
    catalogueTab: string;
    isOpen: boolean;
}>(viewCataloguePanel);

export const closeModalOpened = 'stella/closeModalOpened';
export const closeModalOpenedAction = createAction(closeModalOpened);

export const closeFromCloseModal = 'stella/closeFromCloseModal';
export const closeFromCloseModalAction = createAction(closeFromCloseModal);

export const cancelCloseFromCloseModal = 'stella/cancelCloseFromCloseModal';
export const cancelCloseFromCloseModalAction = createAction(cancelCloseFromCloseModal);

export const shareLinkLoaded = 'stella/shareLinkLoaded';
export const shareLinkLoadedAction = createAction(shareLinkLoaded);
